import client from './client';
import type { QuotaType, QuotaRequestStatus } from '../types';

// ─── Section types ────────────────────────────────────────────────────────────

export interface InsightsSummary {
  photosThisMonth: number;
  quotaHeadroomPercent: number;
  totalDownloads: number;
  processingSuccessRate: number;
}

export interface QuotaEventDetail {
  eventId: string;
  eventName: string;
  photos: { used: number; limit: number };
  albums: { used: number; limit: number };
  ftpAccounts: { used: number; limit: number };
  liveAlbums: { used: number; limit: number };
}

export interface QuotaSection {
  limits: {
    maxEvents: number;
    maxPhotosPerEvent: number;
    maxAlbumsPerEvent: number;
    maxFtpAccountsPerEvent: number;
    maxLiveAlbumsPerEvent: number;
  };
  events: { used: number; limit: number };
  eventDetails: QuotaEventDetail[];
  pendingRequests: { quotaType: QuotaType; status: QuotaRequestStatus }[];
  lowestHeadroomPercent: number;
}

export interface UploadsSection {
  totalAllTime: number;
  totalThisMonth: number;
  trendLast14Days: { date: string; count: number }[];
  bySource: { ftp: number; manual: number };
  byFormat: { jpeg: number; heic: number; raw: number; other: number };
  successRate: number;
  failedByErrorCode: Record<string, number>;
  topPhotographers: { ftpAccountId: string; photographerName: string; count: number }[];
}

export interface ProcessingSection {
  overlays: { processed: number; pending: number; failed: number; successRate: number };
  failedAlbums: { albumId: string; albumName: string; eventId: string; failedCount: number }[];
  faceIndexing: {
    eventId: string;
    eventName: string;
    status: 'idle' | 'running' | 'completed' | 'failed';
    indexedCount: number;
    totalPhotos: number;
    failureCount: number;
    enabled: boolean;
  }[];
  zips: { ready: number; stale: number };
}

export interface EngagementSection {
  totalMembers: number;
  topAlbumsByMembers: { albumId: string; albumName: string; memberCount: number }[];
  totalLikes: number;
  topLikedPhotos: { photoId: string; albumId: string; albumName: string; likeCount: number; thumbnailUrl: string | null }[];
  downloads: {
    total: number;
    byType: { single: number; batch: number; all: number };
    topAlbums: { albumId: string; albumName: string; count: number }[];
    trendLast30Days: { date: string; count: number }[];
  };
  reports: {
    pending: number;
    actioned: number;
    dismissed: number;
    byReason: Record<string, number>;
  };
}

export interface FaceRecognitionSection {
  eventsEnabled: number;
  totalEventsCreated: number;
  totalSearches: number;
  avgMatchesPerSearch: number;
  guestsWithMatches: number;
  googlePhotos: { photosSynced: number; syncJobSuccessRate: number };
}

export interface ContentSection {
  events: { active: number; ended: number };
  albums: { regular: number; live: number; slideshowEnabled: number };
  totalPhotos: number;
  publicGalleries: { active: number; expired: number; passwordProtected: number; open: number };
  overlaysPerEvent: { eventId: string; eventName: string; overlayCount: number }[];
  exportPresets: number;
}

export interface TeamSection {
  activeManagers: number;
  pendingInvitations: number;
  permissionDistribution: { full: number; restricted: number };
  activeFtpThisMonth: number;
  topPhotographers: { ftpAccountId: string; photographerName: string; eventName: string; count: number }[];
}

export interface InsightsData {
  summary: InsightsSummary;
  quota: QuotaSection;
  uploads: UploadsSection;
  processing: ProcessingSection;
  engagement: EngagementSection;
  faceRecognition: FaceRecognitionSection;
  content: ContentSection;
  team: TeamSection;
}

// ─── API call ─────────────────────────────────────────────────────────────────

export const insightsApi = {
  get: () => client.get<InsightsData>('/creator/insights').then((r) => r.data),
};
